import React, { useState, useEffect } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useTranslation } from '../hooks/use-i18n'
import {
  createPaymentIntent,
  getMyPayments,
  type CreatePaymentIntentResponse,
  type PaymentItem,
} from '../services/paymentService'
import config from '../services/config'
import StripeCheckoutForm from '../components/StripeCheckoutForm'

const stripePublishableKey = config.stripe.publishableKey
const stripeKeyMisconfigured =
  stripePublishableKey.startsWith('sk_') || (stripePublishableKey.length > 0 && !stripePublishableKey.startsWith('pk_'))
const stripePromise =
  stripePublishableKey && !stripeKeyMisconfigured ? loadStripe(stripePublishableKey) : null

const PaymentsPage: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [payments, setPayments] = useState<PaymentItem[]>([])
  const [loadingPayments, setLoadingPayments] = useState(true)
  const [amountDollars, setAmountDollars] = useState('')
  const [currency, setCurrency] = useState('eur')
  const [serviceType, setServiceType] = useState('one_time')
  const [creatingIntent, setCreatingIntent] = useState(false)
  const [checkoutIntent, setCheckoutIntent] = useState<CreatePaymentIntentResponse | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [buildInfo, setBuildInfo] = useState<{ builtAt: string; stripeAccountRef: string; stripeMode: string } | null>(null)
  const [backendStripe, setBackendStripe] = useState<{ mode: string; accountRef: string | null } | null>(null)

  useEffect(() => {
    fetch('/build-info.json')
      .then((r) => (r.ok ? r.json() : null))
      .then(setBuildInfo)
      .catch(() => setBuildInfo(null))
    fetch(`${config.baseURL}/api/payments/stripe-mode`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && typeof data.mode === 'string') {
          setBackendStripe({ mode: data.mode, accountRef: data.accountRef ?? null })
        }
      })
      .catch(() => setBackendStripe(null))
  }, [])

  const stripeAccountMismatch =
    buildInfo?.stripeAccountRef &&
    backendStripe?.accountRef &&
    buildInfo.stripeAccountRef !== backendStripe.accountRef

  const fetchPayments = () => {
    setLoadingPayments(true)
    getMyPayments()
      .then(setPayments)
      .catch(() => setPayments([]))
      .finally(() => setLoadingPayments(false))
  }

  useEffect(() => {
    fetchPayments()
  }, [])

  useEffect(() => {
    const success = searchParams.get('payment_success') === '1' || searchParams.get('redirect_status') === 'succeeded'
    if (success) {
      setSuccessMessage(t('payments.successMessage'))
      setSearchParams({}, { replace: true })
      fetchPayments()
    }
  }, [searchParams, setSearchParams, t])

  const handleCreateIntent = async (e: React.FormEvent) => {
    e.preventDefault()
    const amount = Math.round(parseFloat(amountDollars) * 100)
    if (!Number.isFinite(amount) || amount < 50) {
      toast.error(t('payments.minAmount'))
      return
    }
    if (!stripePromise) {
      toast.error(t('payments.stripeNotConfigured'))
      return
    }
    setCreatingIntent(true)
    try {
      const result = await createPaymentIntent({
        amountCents: amount,
        currency,
        serviceType,
        metadata: { source: 'react-brc' },
      })
      setCheckoutIntent(result)
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : null
      toast.error(message || t('payments.errorCreateIntent'))
    } finally {
      setCreatingIntent(false)
    }
  }

  const handleCheckoutSuccess = () => {
    setCheckoutIntent(null)
    setAmountDollars('')
    toast.success(t('payments.successMessage'))
    fetchPayments()
  }

  const handleCheckoutCancel = () => {
    setCheckoutIntent(null)
  }

  if (!stripePromise) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <h1 className="font-display text-display-2 font-bold text-foreground mb-4">{t('payments.title')}</h1>
        <div className="glass-card rounded-2xl p-6 border border-warning/30 bg-warning/5">
          <p className="text-foreground">
            {stripeKeyMisconfigured ? t('payments.stripeSecretKeyInFrontend') : t('payments.stripeNotConfigured')}
          </p>
          <p className="text-muted-foreground text-small mt-2">
            {stripeKeyMisconfigured ? t('payments.stripeSecretKeyInFrontendHint') : t('payments.stripeNotConfiguredHint')}
          </p>
        </div>
      </div>
    )
  }

  const options = checkoutIntent
    ? {
        clientSecret: checkoutIntent.clientSecret,
        appearance: {
          theme: 'stripe' as const,
          variables: {
            colorPrimary: '#2563eb',
            borderRadius: '0.75rem',
          },
        },
      }
    : undefined

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <h1 className="font-display text-display-2 font-bold text-foreground mb-2">{t('payments.title')}</h1>
      <p className="text-muted-foreground text-body-lg mb-8">{t('payments.subtitle')}</p>

      {successMessage && (
        <div className="mb-6 rounded-xl bg-success/10 border border-success/30 px-4 py-3 text-success">
          {successMessage}
        </div>
      )}

      {stripeAccountMismatch && (
        <div className="mb-6 rounded-xl bg-destructive/10 border border-destructive/30 px-4 py-3 text-destructive text-small">
          Stripe key mismatch: this page was built for account <code>{buildInfo?.stripeAccountRef}</code> but the API
          uses <code>{backendStripe?.accountRef}</code>. Hard-refresh (Ctrl+Shift+R) or redeploy after fixing env vars.
        </div>
      )}

      {!checkoutIntent ? (
        <section className="glass-card rounded-2xl p-6 mb-8">
          <h2 className="font-display text-display-2 font-semibold text-foreground mb-4">{t('payments.makePayment')}</h2>
          <form onSubmit={handleCreateIntent} className="space-y-4">
            <div>
              <label className="block text-small font-medium text-foreground mb-1.5">{t('payments.amount')}</label>
              <input
                type="number"
                min="0.50"
                step="0.01"
                value={amountDollars}
                onChange={(e) => setAmountDollars(e.target.value)}
                placeholder="0.00"
                className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                required
              />
              <p className="text-muted-foreground text-small mt-1">{t('payments.amountHint')}</p>
            </div>
            <div>
              <label className="block text-small font-medium text-foreground mb-1.5">{t('payments.currency')}</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="usd">USD</option>
                <option value="eur">EUR</option>
                <option value="gbp">GBP</option>
              </select>
            </div>
            <div>
              <label className="block text-small font-medium text-foreground mb-1.5">{t('payments.serviceType')}</label>
              <select
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="one_time">{t('payments.serviceOneTime')}</option>
                <option value="subscription">{t('payments.serviceSubscription')}</option>
                <option value="donation">{t('payments.serviceDonation')}</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={creatingIntent}
              className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-50 transition-all"
            >
              {creatingIntent ? t('payments.creating') : t('payments.continueToPayment')}
            </button>
          </form>
        </section>
      ) : (
        <section className="glass-card rounded-2xl p-6 mb-8">
          <h2 className="font-display text-display-2 font-semibold text-foreground mb-4">{t('payments.completePayment')}</h2>
          <Elements key={checkoutIntent.clientSecret} stripe={stripePromise} options={options}>
            <StripeCheckoutForm
              amountCents={checkoutIntent.amountCents}
              currency={checkoutIntent.currency}
              onSuccess={handleCheckoutSuccess}
              onCancel={handleCheckoutCancel}
            />
          </Elements>
        </section>
      )}

      <section className="glass-card rounded-2xl p-6">
        <h2 className="font-display text-display-2 font-semibold text-foreground mb-4">{t('payments.paymentHistory')}</h2>
        {loadingPayments ? (
          <p className="text-muted-foreground">{t('payments.loading')}</p>
        ) : payments.length === 0 ? (
          <p className="text-muted-foreground">{t('payments.noPayments')}</p>
        ) : (
          <ul className="space-y-3">
            {payments.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-muted/10 px-4 py-3"
              >
                <div>
                  <span className="font-medium text-foreground">
                    {(p.amountCents / 100).toFixed(2)} {p.currency.toUpperCase()}
                  </span>
                  <span className="text-muted-foreground text-small ml-2">• {p.serviceType}</span>
                </div>
                <span
                  className={`text-small font-medium px-2 py-0.5 rounded-full ${
                    p.status === 'succeeded'
                      ? 'bg-success/20 text-success'
                      : p.status === 'failed'
                      ? 'bg-destructive/20 text-destructive'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {p.status}
                </span>
                <div className="w-full text-muted-foreground text-small">
                  {new Date(p.createdAt).toLocaleString()}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

export default PaymentsPage
